import React, { useEffect, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import Form from "react-bootstrap/Form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiRequest } from "../apiService/allApi";

import { FaBars } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Price ranges definition (moved outside component to prevent re-creation on every render)
const priceRanges = {
  "Below 500": { min: 0, max: 500 },
  "501 - 1000": { min: 501, max: 1000 },
  "1001 - 1500": { min: 1001, max: 1500 },
  "1501 - 2000": { min: 1501, max: 2000 },
  "2001 - 2500": { min: 2001, max: 2500 },
  "2501 - 4000": { min: 2501, max: 4000 },
  "4001 - 6500": { min: 4001, max: 6500 },
  "6501 and above": { min: 6501, max: Infinity },
};

const CategoryPage = () => {
  const [showFilter, setShowFilter] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔹 FILTER OPTIONS FROM API (now dynamic array)
  const [filterOptions, setFilterOptions] = useState([]);

  // 🔹 FILTER STATE (dynamic based on API)
  const [filters, setFilters] = useState({
    price: [],
    category: [],
  });

  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") || "";

  useEffect(() => {
    if (!products.length) return; // 🔑 wait till products loaded

    const type = searchParams.get("type");

    const categoryMap = {
      eyeglasses: "Eyeglasses",
      sunglasses: "Sunglasses",
      "power-sunglasses": "Power Sunglasses",
      "computer-glasses": "Computer Glasses",
      "contact-lenses": "Contact Lenses",
      "cricket-glasses": "Cricket Glasses",
    };

    if (type && categoryMap[type]) {
      setFilters((prev) => ({
        ...prev,
        category: [categoryMap[type]],
      }));
    }
  }, [searchParams, products]);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ================= API FETCH (FIXED) =================
  const productList = async () => {
    setLoading(true);

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    try {
      const res = await apiRequest(
        // `api/products/front?page=1&limit=20&price=${50000}`,
        `api/products`,

        "GET",
        null,
        headers,
      );
      if (res.data) {
        setLoading(false);
        setProducts(res.data?.products);
        setFilteredProducts(res.data?.products);
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
    } finally {
    }
  };

  // ================= FETCH FILTER ATTRIBUTES =================
  const fetchFilterAttributes = async () => {
    try {
      const res = await apiRequest(
        "api/attributes?type=attribute&option=Dropdown&option1=Radio",
        "GET",
      );
      if (res.data) {
        setFilterOptions(res.data);
        // Initialize filters state dynamically
        const initialFilters = { price: [], category: [] };
        res.data.forEach((attr) => {
          initialFilters[attr.name?.en] = [];
        });
        setFilters(initialFilters);
      }
    } catch (e) {
      console.log("Error fetching filter attributes:", e);
    }
  };

  // ================= FILTER HANDLER =================
  const handleFilterChange = (group, value, checked) => {
    setFilters((prev) => ({
      ...prev,
      [group]: checked
        ? [...prev[group], value]
        : prev[group].filter((v) => v !== value),
    }));
  };

  // ================= RESET FILTERS =================
  const resetFilters = () => {
    const initialFilters = { price: [], category: [] };
    filterOptions.forEach((attr) => {
      initialFilters[attr.name?.en] = [];
    });
    setFilters(initialFilters);
  };

  // ================= APPLY FILTERS =================
  useEffect(() => {
    let result = [...products];

    // Apply search filter first if searchQuery exists
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((product) => {
        const title = product.title?.en?.toLowerCase() || "";
        const description = product.description?.en?.toLowerCase() || "";
        const categories =
          product.categories
            ?.map((cat) => cat.name?.en?.toLowerCase())
            .join(" ") || "";
        return (
          title.includes(query) ||
          description.includes(query) ||
          categories.includes(query)
        );
      });
    }

    Object.keys(filters).forEach((key) => {
      if (filters[key].length > 0) {
        if (key === "price") {
          // Special handling for price filter
          result = result.filter((product) => {
            const productPrice = product.prices?.price || 0;
            return filters[key].some((range) => {
              const { min, max } = priceRanges[range];
              return productPrice >= min && productPrice <= max;
            });
          });
        } else if (key === "category") {
          // Category-based filtering
          result = result.filter((product) =>
            product.categories?.some((cat) =>
              filters[key].includes(cat.name?.en),
            ),
          );
        } else {
          // Attribute-based filtering for frameStyle, frameShape, etc.
          // Find the attribute from filterOptions
          const attr = filterOptions.find((a) => a.name?.en === key);
          if (attr) {
            // Get selected variant names
            const selectedNames = filters[key];
            // Check if product has any of the selected variant names in its variants
            result = result.filter((product) => {
              if (!product.variants) return false;
              return selectedNames.some((name) =>
                product.variants.some((variant) => {
                  const variantValue = Object.values(variant)[0];
                  return variantValue?.name === name;
                }),
              );
            });
          }
        }
      }
    });

    setFilteredProducts(result);
  }, [filters, products, priceRanges, searchQuery]);

  useEffect(() => {
    productList();
    fetchFilterAttributes();
  }, []);

  // ================= PRODUCT CLICK =================
  const handleProductClick = (id) => {
    navigate(`/product/${id}`);
  };

  // 🔹 ADD TO CART (sessionStorage)
  const addToCart = (product) => {
    let cart = JSON.parse(sessionStorage.getItem("cart")) || [];

    // 🔴 If stock finished
    if (product.stock <= 0) {
      toast.error("❌ Product Out of Stock!");
      return;
    }

    const existing = cart.find((item) => item._id === product._id);

    if (existing) {
      // 🟡 If qty reached stock
      if (existing.qty >= product.stock) {
        toast.error("out of stock!");
        return;
      }

      existing.qty += 1;
    } else {
      cart.push({
        _id: product._id,
        name: product.title?.en,
        price: product.prices?.price,
        image: product.image?.[0],
        qty: 1,
        stock: product.stock, // ✅ important
      });
    }

    sessionStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success("Product added to cart 🛒");
  };
  console.log(filteredProducts, "ssore");

  return (
    <>
      <div className="container my-3">
        <div className="about-breadcrumb">
          Home <span>›</span> Eyeglasses
        </div>

        <div className="mb-5 cate-info">
          <h1>
            {searchQuery ? `Search Results for "${searchQuery}"` : "Eyeglasses"}
          </h1>
          <p>
            {searchQuery
              ? `Showing products matching your search.`
              : "Eyeglasses that blend style and comfort for every face."}
          </p>
        </div>

        <div className="row">
          {/* filter-bar-btn */}

          <div className=" d-lg-none  d-flex align-items-center justify-content-between pt-3 px-2">
            <h6 className="cate-filter-title mb-0">Filters</h6>
            <div
              className="filter-bar"
              onClick={() => setIsActive(!isActive)}
              style={{ cursor: "pointer" }}
            >
              <FaBars />
            </div>
          </div>

          {/* ================= LEFT FILTER UI ================= */}
          <div className="col-lg-3 left-bar1 mb-lg-0 mb-4">
            <div
              className={`left-bar-cate ${isActive ? "active" : ""}`}
              onClick={() => setIsActive(false)}
            >
              <div
                className="d-flex align-items-center justify-content-between pt-3 px-2"
                onClick={(e) => e.stopPropagation()}
              >
                <h6 className="cate-filter-title">Filters</h6>
                <button className="btn1" onClick={resetFilters}>
                  Reset
                </button>
              </div>

              <Accordion
                defaultActiveKey="0"
                alwaysOpen
                onClick={(e) => e.stopPropagation()}
              >
                {/* Dynamic Filters from API */}
                {filterOptions.map((attr, index) => (
                  <Accordion.Item
                    eventKey={index.toString()}
                    key={attr.name?.en}
                  >
                    <Accordion.Header>{attr.name?.en}</Accordion.Header>
                    <Accordion.Body className={index === 0 ? "px-3" : ""}>
                      <Form>
                        {attr.variants?.map((variant, i) => (
                          <div
                            className="form-check d-flex align-items-center mb-2"
                            key={i}
                          >
                            <label className="form-check-label me-auto">
                              {variant.name?.en}
                            </label>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              onChange={(e) =>
                                handleFilterChange(
                                  attr.name?.en,
                                  variant.name?.en,
                                  e.target.checked,
                                )
                              }
                            />
                          </div>
                        ))}
                      </Form>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}

                {/* Price */}
                <Accordion.Item eventKey={filterOptions.length.toString()}>
                  <Accordion.Header>Price</Accordion.Header>
                  <Accordion.Body>
                    <Form>
                      {Object.keys(priceRanges).map((range, i) => (
                        <div
                          className="form-check d-flex align-items-center mb-2"
                          key={i}
                        >
                          <label className="form-check-label me-auto">
                            {range}
                          </label>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            onChange={(e) =>
                              handleFilterChange(
                                "price",
                                range,
                                e.target.checked,
                              )
                            }
                          />
                        </div>
                      ))}
                    </Form>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          </div>

          {/* ================= PRODUCTS ================= */}
          <div className="col-lg-9">
            <div className="row">
              {loading && <p>Loading products...</p>}

              {!loading && filteredProducts.length === 0 && (
                <p>No products found</p>
              )}

              {filteredProducts.map((item) => (
                <div className="col-lg-4 col-sm-6 mb-3" key={item._id}>
                  <div className="product-card mb-3 group">
                    <span className="badge stock">
                      Stock : <strong>{item.stock}</strong>
                    </span>

                    <div
                      className="product-image"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleProductClick(item._id)}
                    >
                      <img src={item.image?.[0]} alt={item.title?.en} />
                    </div>

                    <div className="product-content">
                      <h2
                        className="product-title"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleProductClick(item._id)}
                      >
                        {item.title?.en}
                      </h2>

                      <div className="product-footer">
                        <div className="price">
                          <span className="current">₹{item.prices?.price}</span>
                          <del className="old">
                            ₹{item.prices?.originalPrice}
                          </del>
                        </div>
                        <button
                          className="cart-btn"
                          disabled={item.stock <= 0}
                          onClick={() => addToCart(item)}
                          aria-label="Add to cart"
                        >
                          <svg
                            viewBox="0 0 512 512"
                            width="18"
                            height="18"
                            fill="currentColor"
                          >
                            <path d="M460 160h-88v-12A116.13 116.13 0 00258.89 32h-5.78A116.13 116.13 0 00140 148v12H52a4 4 0 00-4 4v300a16 16 0 0016 16h384a16 16 0 0016-16V164a4 4 0 00-4-4zm-280-11c0-41.84 33.41-76.56 75.25-77A76.08 76.08 0 01332 148v12H180zm156 187h-64v64h-32v-64h-64v-32h64v-64h32v64h64z" />
                          </svg>
                        </button>{" "}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={2000}
        closeOnClick
        pauseOnHover
      />
    </>
  );
};

export default CategoryPage;
